<script lang="ts" setup>
import { computed } from "#imports";

export interface FooterColumnData {
  xPos: number;
  width: number;
  content: string;
}

export interface FooterRowData {
  fixed: boolean;
  showTitle: boolean;
  title: string;
  columns?: FooterColumnData[];
}

export interface FooterData {
  rows?: FooterRowData[];
}

const props = withDefaults( defineProps<{
  /**
   * Footer content to render
   */
  content?: FooterData | null;
  titleTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div" | null;
}>(), {
  titleTag: "h3",
} );

/**
 * List of footer rows
 */
const rows = computed( () => {
  return props.content?.rows || [];
} );

/**
 * Whether we have any content to display
 */
const hasContent = computed( () => {
  return ( rows.value.length > 0 ) && ( ( rows.value[0].columns?.length ?? 0 ) > 0 );
} );
</script>

<template>
  <div class="footer-content">
    <div class="footer-content__prefix">
      <slot name="prefix" />
    </div>
    <div
      v-if="hasContent && content"
      class="footer-content__rows">
      <div
        v-for="(row, rowIndex) in content.rows"
        :key="rowIndex"
        class="footer-content__row"
        :data-index="rowIndex"
        :class="{ 'footer-content__row--fixed': row.fixed }">
        <component
          :is="titleTag"
          v-if="row.showTitle"
          class="footer-content__row-title">
          {{ row.title }}
        </component>
        <div
          v-if="row.columns"
          class="footer-content__columns">
          <div
            v-for="(column, colIndex) in row.columns"
            :key="colIndex"
            class="footer-content__column"
            :data-index="colIndex"
            :data-xpos="column.xPos + 1"
            :data-width="column.width">
            <RichText
              class="footer-content__column-content"
              :content="column.content" />
          </div>
        </div>
      </div>
    </div>
    <div class="footer-content__suffix">
      <slot name="suffix" />
    </div>
  </div>
</template>

<style lang="scss">
@use "sass:math";

  .footer-content {
    &__rows {
      display: flex;
      flex-direction: column;
    }

    &__columns {
      display: grid;
      grid-auto-rows: minmax(min-content, max-content);
      grid-template-columns: repeat(12, minmax(0, 1fr));
    }

    &__column {
      overflow-x: hidden;
      // Mobile stetches gives all columns a width of 12 so they will get their own row
      grid-column-start: 1;
      grid-column-end: span 12;

      @media (min-width: 768px){
        @for $colIndex from 1 through 12 {
          // For tablet sized screens stretch each column across two columns.
          // Also move columns from x-position 7 to 12 to a new row and shift them
          // back to x-position 1 to 6
          $transformedColIndex: $colIndex;
          @if ($colIndex > 6) {
            $transformedColIndex: $colIndex - 6;
          }
          $transformedColIndex: math.div(12, 6) * $transformedColIndex;

          &[data-xpos="#{$colIndex}"] {
            grid-column-start: $transformedColIndex;
          }

          &[data-width="#{$colIndex}"] {
            grid-column-end: span $transformedColIndex;
          }
        }
      }

      @media (min-width: 1200px){
        @for $colIndex from 1 through 12 {
          // Desktop mode gives columns their normal position and width
          &[data-xpos="#{$colIndex}"] {
            grid-column-start: $colIndex;
          }

          &[data-width="#{$colIndex}"] {
            grid-column-end: span $colIndex;
          }
        }
      }
    }
  }
  </style>
